import React, {useState, useEffect, useLayoutEffect} from 'react';
import {SafeAreaView, View, ActivityIndicator} from 'react-native';
import moment from 'moment';
import _ from 'lodash';
import {Divider} from 'react-native-paper';

import sy from '~/styles';
import {
  Text,
  PrimaryButton,
  LinearProgress,
  ReplacementText,
  Grouping,
  Checkbox,
  Link,
} from '~/components/controls';
import {
  service_request as serviceRequestApi,
  car as carApi,
} from '~/api/public';
import config from '~/configuration';
import {useDispatch, useSelector} from '~/lib/hooks';
import Format from '~/lib/format';
import {addJobReset} from '~/actions';
import {PublicContentView} from '~/components/shared';

import EventIcon from '~/images/md-icons/event/materialicons/24px.svg';
import DirectionsCarIcon from '~/images/md-icons/directions_car/materialicons/24px.svg';
import BusinessIcon from '~/images/md-icons/business/materialicons/24px.svg';
import PersonIcon from '~/images/md-icons/person/materialicons/24px.svg';
import AssignmentIcon from '~/images/md-icons/assignment/materialicons/24px.svg';
import EmojiTransportationIcon from '~/images/md-icons/emoji_transportation/materialicons/24px.svg';
import AssignementReturnIcon from '~/images/md-icons/assignment_return/materialicons/24px.svg';
import HomeFilledIcon from '~/images/md-icons/home_filled/materialicons/24px.svg';

const Confirm = ({navigation}) => {
  const dispatch = useDispatch();
  const dealer = useSelector((state) => state.dealer);
  const initials = useSelector((state) => state.initials);
  const firstName = useSelector((state) => state.firstName);
  const lastName = useSelector((state) => state.lastName);
  const email = useSelector((state) => state.email);
  const phone_number = useSelector((state) => state.phone_number);
  const date = useSelector((state) => state.date);
  const license = useSelector((state) => state.license);
  const mileage = useSelector((state) => state.mileage);
  const services = useSelector((state) => state.services);
  const replacement = useSelector((state) => state.replacement);
  const pickup_location = useSelector((state) => state.pickup_location);
  const is_company_car = useSelector((state) => state.is_company_car);
  const company = useSelector((state) => state.company);
  const is_lease = useSelector((state) => state.is_lease);
  const lessor = useSelector((state) => state.lessor);

  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checked, setChecked] = useState(
    services?.map((service) => service.id) ?? [],
  );

  useLayoutEffect(() => {
    navigation?.setOptions({
      headerShown: true,
      headerStatusBarHeight: 0,
      headerTitleAlign: 'center',
      headerTitle: () => (
        <LinearProgress style={{width: 126}} step={7} total={7} />
      ),
    });
  }, [navigation]);

  useEffect(() => {
    const fetch = async () => {
      if (license) {
        const car = await carApi.fetch(license);
        setCar(car);
      }

      setLoading(false);
    };

    fetch();
  }, [license, dealer.id]);

  const onConfirm = async () => {
    const result = await serviceRequestApi.new(dealer.id, {
      company: is_company_car ? company : null,
      is_company_car,
      initials,
      firstName,
      lastName,
      email,
      phone_number,
      date,
      license,
      mileage,
      services: checked,
      replacement,
      pickup_location: replacement === 'transport' ? pickup_location : null,
    });

    if (result.success) {
      if (!config.development) {
        dispatch(addJobReset());
      }

      navigation.popToTop();
      navigation.replace('Overview', {
        screen: 'Chat',
        params: {
          request_id: result.request_id,
        },
      });
    }
  };

  const isValid =
    (!is_company_car || !!company) &&
    initials?.length &&
    lastName?.length &&
    phone_number.length &&
    email.length &&
    checked.length;

  return (
    <>
      <SafeAreaView style={sy.mainView}>
        {loading && <ActivityIndicator size="large" color="#231fda" />}
        {!loading && (
          <>
            <PublicContentView footerStyle={{borderTopWidth: 0}}>
              {{
                content: (
                  <>
                    <Divider />
                    <View style={[sy['p-4']]}>
                      <Text style={sy.xxLarge}>Aanvraag controleren</Text>
                      <Text style={[sy.large, sy['mt-4']]}>
                        Je aanvraag kan je hier checken, eventueel wijzigen en
                        als alles juist is verzenden.
                      </Text>
                    </View>
                    <View style={[sy.contentView]}>
                      <Grouping
                        icon={<EventIcon fill="#4a4a49" />}
                        header={
                          <>
                            {moment(date).format('dddd D MMMM')}
                            &nbsp;&middot;&nbsp;
                            {moment(date).format('H:mm')}&nbsp;uur
                          </>
                        }>
                        <View
                          style={[
                            sy['p-4'],
                            sy['flex-row'],
                            sy['justify-between'],
                          ]}>
                          <Text>Datum</Text>
                          <Link
                            onPress={() =>
                              navigation.push('Date', {
                                dealer_id: dealer.id,
                                is_change: true,
                              })
                            }>
                            {moment(date).format('dddd D MMMM YYYY')}
                          </Link>
                        </View>
                        <Divider style={{backgroundColor: '#f2f2f2'}} />
                        <View
                          style={[
                            sy['p-4'],
                            sy['flex-row'],
                            sy['justify-between'],
                          ]}>
                          <Text>Aflevertijd</Text>
                          <Link
                            onPress={() =>
                              navigation.push('Time', {
                                dealer_id: dealer.id,
                                is_change: true,
                              })
                            }>
                            {moment(date).format('H:mm')}
                          </Link>
                        </View>
                      </Grouping>
                      <Grouping
                        icon={<DirectionsCarIcon />}
                        header={
                          <>
                            {car?.name}&nbsp;&middot;&nbsp;
                            {Format.license(license)}
                          </>
                        }>
                        <View
                          style={[
                            sy['p-4'],
                            sy['flex-row'],
                            sy['justify-between'],
                          ]}>
                          <Text>{car?.name}</Text>
                          <Link
                            onPress={() =>
                              navigation.push('Car', {
                                dealer_id: dealer.id,
                                is_change: true,
                              })
                            }>
                            {Format.license(license)}
                          </Link>
                        </View>
                        <Divider style={{backgroundColor: '#f2f2f2'}} />
                        <View
                          style={[
                            sy['p-4'],
                            sy['flex-row'],
                            sy['justify-between'],
                          ]}>
                          <Text>Kilometerstand</Text>
                          <Link
                            onPress={() =>
                              navigation.push('Car', {
                                dealer_id: dealer.id,
                                is_change: true,
                              })
                            }>
                            {Format.number(mileage, 0, true)}
                          </Link>
                        </View>
                      </Grouping>
                      <Grouping
                        icon={<BusinessIcon fill="#4a4a49" />}
                        header={
                          is_lease ? lessor : company?.name ?? 'Particulier'
                        }>
                        <View
                          style={[
                            sy['p-4'],
                            sy['flex-row'],
                            sy['justify-between'],
                          ]}>
                          <Text>Eigendom</Text>
                          <Link
                            onPress={() =>
                              navigation.push('Car', {
                                dealer_id: dealer.id,
                                is_change: true,
                              })
                            }>
                            {is_lease
                              ? lessor
                              : is_company_car === false
                              ? 'Particulier'
                              : 'Zakelijk'}
                          </Link>
                        </View>
                      </Grouping>
                      <Grouping
                        icon={<PersonIcon fill="#4a4a49" />}
                        header={
                          <>
                            {firstName ?? initials}&nbsp;{lastName}
                          </>
                        }>
                        <View
                          style={[
                            sy['p-4'],
                            sy['flex-row'],
                            sy['justify-between'],
                          ]}>
                          <Text>Voorletter(s)</Text>
                          <Link
                            onPress={() =>
                              navigation.push('User', {
                                dealer_id: dealer.id,
                                is_change: true,
                              })
                            }>
                            {initials}
                          </Link>
                        </View>
                        <View
                          style={[
                            sy['p-4'],
                            sy['flex-row'],
                            sy['justify-between'],
                          ]}>
                          <Text>Voornaam</Text>
                          <Link
                            onPress={() =>
                              navigation.push('User', {
                                dealer_id: dealer.id,
                                is_change: true,
                              })
                            }>
                            {firstName}
                          </Link>
                        </View>
                        <View
                          style={[
                            sy['p-4'],
                            sy['flex-row'],
                            sy['justify-between'],
                          ]}>
                          <Text>Achternaam</Text>
                          <Link
                            onPress={() =>
                              navigation.push('User', {
                                dealer_id: dealer.id,
                                is_change: true,
                              })
                            }>
                            {lastName}
                          </Link>
                        </View>
                        <View
                          style={[
                            sy['p-4'],
                            sy['flex-row'],
                            sy['justify-between'],
                          ]}>
                          <Text>Mobiele telefoonnummer</Text>
                          <Text>{phone_number}</Text>
                        </View>
                        <View
                          style={[
                            sy['p-4'],
                            sy['flex-row'],
                            sy['justify-between'],
                          ]}>
                          <Text>Emailadres</Text>
                          <Link
                            onPress={() =>
                              navigation.push('Replacement', {
                                dealer_id: dealer.id,
                                is_change: true,
                              })
                            }>
                            {email}
                          </Link>
                        </View>
                      </Grouping>
                      <Divider />
                      <View style={[sy['p-4'], sy['flex-row'], sy['gap-8']]}>
                        <AssignmentIcon fill="#4a4a49" />
                        <View
                          style={[
                            sy['flex-1'],
                            sy['flex-row'],
                            sy['items-center'],
                            sy['justify-between'],
                          ]}>
                          <Text style={sy.medium}>Opdracht</Text>
                          {!is_lease && (
                            <Text style={sy.medium}>Prijs volgt</Text>
                          )}
                        </View>
                      </View>
                      <Divider style={{backgroundColor: '#f2f2f2'}} />
                      <View style={[sy['p-4'], sy['gap-4']]}>
                        {services
                          ?.sort((a, b) => {
                            return !_.isNumber(a.sort_number)
                              ? 1
                              : !_.isNumber(b.sort_number)
                              ? -1
                              : a.sort_number - b.sort_number;
                          })
                          .map((service, index, array) => (
                            <React.Fragment key={service.id}>
                              <View style={[sy['flex-row'], sy['gap-8']]}>
                                <Checkbox
                                  checked={checked.includes(service.id)}
                                  onPress={() => {
                                    if (checked.includes(service.id)) {
                                      setChecked(
                                        checked.filter(
                                          (id) => id !== service.id,
                                        ),
                                      );
                                    } else {
                                      setChecked([...checked, service.id]);
                                    }
                                  }}
                                />
                                <View
                                  style={[
                                    sy['flex-1'],
                                    sy['flex-row'],
                                    sy['items-center'],
                                    sy['justify-between'],
                                  ]}>
                                  <Text>{service.title}</Text>
                                  {!is_lease && (
                                    <Text style={sy.smallRegular}>
                                      {_.isNumber(service.price)
                                        ? Format.price(service.price)
                                        : 'Prijs volgt'}
                                    </Text>
                                  )}
                                </View>
                              </View>
                              {index < array.length - 1 && (
                                <Divider
                                  style={[
                                    sy['ml-14'],
                                    {backgroundColor: '#f2f2f2'},
                                  ]}
                                />
                              )}
                            </React.Fragment>
                          ))}
                      </View>
                      <Grouping
                        icon={<EmojiTransportationIcon fill="#4a4a49" />}
                        header={
                          <ReplacementText
                            style={sy.medium}
                            type={replacement}
                          />
                        }>
                        {replacement === 'transport' && pickup_location && (
                          <View
                            style={[
                              sy['flex-row'],
                              sy['flex-1'],
                              sy['items-center'],
                            ]}>
                            <View style={[sy['p-4']]}>
                              <HomeFilledIcon
                                fill="#4A4A49"
                                width={24}
                                height={24}
                              />
                            </View>
                            <View style={[sy['flex-1'], sy['px-4']]}>
                              <Link
                                onPress={() =>
                                  navigation.push('Replacement', {
                                    dealer_id: dealer.id,
                                    is_change: true,
                                  })
                                }>
                                {pickup_location.address}
                              </Link>
                            </View>
                          </View>
                        )}
                      </Grouping>
                      <Divider />
                    </View>
                  </>
                ),
                footer: (
                  <>
                    <Divider />
                    <View style={sy.footerView}>
                      <PrimaryButton
                        disabled={!isValid}
                        clickGuardTimeout={5 * 60 * 1000}
                        icon={() => (
                          <AssignementReturnIcon
                            fill="#ffffff"
                            width={18}
                            height={18}
                          />
                        )}
                        onPress={onConfirm}>
                        Verzenden
                      </PrimaryButton>
                    </View>
                  </>
                ),
              }}
            </PublicContentView>
          </>
        )}
      </SafeAreaView>
    </>
  );
};

export default Confirm;
